import Vue from 'vue'
import markdownit from 'markdown-it'
import { createOpencodeClient } from '../sdk/opencodeClient'
import {
  formatMessages,
  normalizeProcessPart,
  normalizeReasoningPlain,
  messageHtmlToPlain
} from './chatMessageUtils'

/** 判断错误是否属于用户中止 / Abort，用于不展示「处理失败」类文案。 */
function isStreamAbortError (error) {
  if (!error) return false
  if (error.name === 'AbortError') return true
  const m = String(error.message != null ? error.message : error).trim()
  if (!m) return false
  const l = m.toLowerCase()
  return l === 'aborted'
}

/** 解析全局 SSE 单行 payload，兼容 { data, payload } 等包装形态。 */
function unwrapGlobalEventRow (row) {
  const payload = row && row.data !== undefined ? row.data : row
  if (payload && payload.payload) {
    return {
      event: payload.payload,
      directory: payload.directory
    }
  }
  return {
    event: payload,
    directory: payload && payload.directory ? payload.directory : null
  }
}

/** 从字符串或 { id | sessionId } 解析出会话 id。 */
function resolveSessionId (sessionOrId) {
  if (sessionOrId == null) return null
  if (typeof sessionOrId === 'string') return sessionOrId
  if (typeof sessionOrId === 'object') {
    return sessionOrId.id || sessionOrId.sessionId || null
  }
  return null
}

/** 深拷贝 initialMessages，避免宿主与内部共享同一引用。 */
function cloneInitialMessages (list) {
  try {
    return JSON.parse(JSON.stringify(list || []))
  } catch (e) {
    return []
  }
}

/**
 * Headless OpenCode 会话控制器（与 AiChatPanel 行为对齐，无内置 UI）。
 * 支持 Vue 2.5+：2.6+ 用 `Vue.observable`；2.5 用无 el 的 `new Vue({ data })` 的 `$data` 作响应式根（行为一致，闭包会持有内部 Vue 以免被 GC）。
 *
 * @param {object} options 与 AiChatPanel props 对齐的配置，外加生命周期回调
 * @returns {object} 可绑定模板的响应式对象 + 方法
 */
export function createAiChatController (rawOptions = {}) {
  const options = Object.assign(
    {
      opencodeUrl: '/opencode',
      opencodeDirectory: '/home/opencode/app',
      opencodeSystem: '',
      opencodeAgent: 'build',
      opencodeModelID: 'GLM-5',
      opencodeProviderID: 'jdprovider',
      opencodeHeaders: {},
      toast: undefined,
      confirm: undefined,
      source: 'crm',
      userId: '',
      maxSessionCount: 10,
      initialMessages: [],
      onSessionIdle: undefined,
      onError: undefined,
      onTitleSynced: undefined
    },
    rawOptions || {}
  )

  const md = markdownit({
    breaks: true,
    html: false
  })

  let disposed = false

  /** 控制器状态初始快照（与 Vue 2.6 observable / 2.5 根 data 共用同一对象引用）。 */
  const initialState = {
    sessionList: [],
    activeSessionId: null,
    messages: [],
    isCurrentSessionLoading: false,
    hasCurrentSessionReplyContent: false,
    activeGenerationBySession: {},
    hasReplyContentBySession: {},
    streamPartTypesBySession: {},
    opencodeClient: null,
    sseAbortController: null,
    streamFinalized: true,
    activeAssistantMessageIndex: -1,
    activeAssistantText: '',
    streamingAssistantMessageIds: [],
    streamingUserMessageIds: [],
    assistantPartTextMap: {},
    assistantPartOrder: [],
    assistantProcessPartsMap: {},
    assistantProcessPartOrder: [],
    childSessionBindingsBySession: {},
    messageRoleBySession: {},
    userInitiatedStop: false,
    streamPartIdTypes: {},
    isFirstUserMessage: false
  }

  let ctrl
  if (typeof Vue.observable === 'function') {
    ctrl = Vue.observable(initialState)
  } else {
    const vm = new Vue({
      data () {
        return initialState
      }
    })
    ctrl = vm.$data
    // 无 el 实例仅用于响应式；挂到非枚举属性上，避免仅暴露 $data 时 Vue 实例被 GC。
    Object.defineProperty(ctrl, '__aiChatShadowVm', {
      value: vm,
      enumerable: false,
      configurable: true
    })
  }

  /** localStorage 中「会话列表」键名（按 source 区分）。 */
  function sessionIdListStorageKey () {
    return `opencodeSessionList_${options.source}`
  }

  /** localStorage 中「上次活跃会话 id」键名（按 source 区分）。 */
  function lastActiveSessionIdKey () {
    return `opencodeLastActiveSessionId_${options.source}`
  }

  /** 根据当前 activeSessionId 与各会话生成态，同步 isCurrentSessionLoading / hasCurrentSessionReplyContent。 */
  function syncLoadingFlags () {
    const sid = ctrl.activeSessionId
    ctrl.isCurrentSessionLoading = !!(sid && ctrl.activeGenerationBySession[sid])
    ctrl.hasCurrentSessionReplyContent = !!(sid && ctrl.hasReplyContentBySession[sid])
  }

  /** 轻提示：优先 options.toast，否则 alert。 */
  function showToast (payload) {
    const opts = typeof payload === 'string' ? { message: payload } : (payload || {})
    const message = opts.message
    if (!message) return false
    if (typeof options.toast === 'function') {
      options.toast(opts)
      return true
    }
    try {
      window.alert(message)
      return true
    } catch (e) {
      return false
    }
  }

  /** 删除确认：优先 options.confirm，否则 window.confirm。 */
  async function confirmDelete (dialogOptions = {}) {
    const message =
      dialogOptions.message != null ? dialogOptions.message : '确定删除该会话？'
    const title = dialogOptions.title != null ? dialogOptions.title : ''
    if (typeof options.confirm === 'function') {
      try {
        return !!(await options.confirm({ message, title }))
      } catch (e) {
        return false
      }
    }
    try {
      return window.confirm(message)
    } catch (e) {
      return false
    }
  }

  /** 读取当前 userId 对应的上次活跃会话 id。 */
  function readLastActiveSessionId () {
    try {
      const raw = localStorage.getItem(lastActiveSessionIdKey())
      if (!raw) return null
      const map = JSON.parse(raw) || {}
      return map[options.userId] || null
    } catch (e) {
      return null
    }
  }

  /** 持久化当前 userId 的上次活跃会话 id。 */
  function persistLastActiveSessionId (id) {
    if (!id || !options.userId) return
    try {
      const key = lastActiveSessionIdKey()
      const map = JSON.parse(localStorage.getItem(key)) || {}
      map[options.userId] = id
      localStorage.setItem(key, JSON.stringify(map))
    } catch (e) {
      console.warn('persistLastActiveSessionId', e)
    }
  }

  /** 清除当前 userId 的上次活跃会话记录。 */
  function clearLastActiveSessionId () {
    if (!options.userId) return
    try {
      const key = lastActiveSessionIdKey()
      const map = JSON.parse(localStorage.getItem(key)) || {}
      if (map[options.userId] == null) return
      delete map[options.userId]
      localStorage.setItem(key, JSON.stringify(map))
    } catch (e) {
      console.warn('clearLastActiveSessionId', e)
    }
  }

  /** 将存储中的单条会话项规范为 { sessionId, title }。 */
  function normalizeStoredSessionItem (item) {
    if (!item) return null
    if (typeof item === 'string') {
      return { sessionId: item, title: '' }
    }
    if (typeof item === 'object') {
      const sessionId = item.sessionId || item.id || ''
      if (!sessionId) return null
      return {
        sessionId,
        title: item.title ? String(item.title).trim() : ''
      }
    }
    return null
  }

  /** 规范化存储中的会话数组并去重。 */
  function normalizeStoredSessionList (list) {
    if (!Array.isArray(list)) return []
    const uniqueMap = {}
    const sessions = []
    for (const item of list) {
      const normalized = normalizeStoredSessionItem(item)
      if (!normalized || !normalized.sessionId || uniqueMap[normalized.sessionId]) continue
      uniqueMap[normalized.sessionId] = true
      sessions.push(normalized)
    }
    return sessions
  }

  /** 从 localStorage 读取当前用户的会话列表（规范化后）。 */
  function getStoredSessions () {
    if (!options.userId) return []
    try {
      const map = JSON.parse(localStorage.getItem(sessionIdListStorageKey())) || {}
      return normalizeStoredSessionList(map[options.userId])
    } catch (e) {
      console.warn('getStoredSessions', e)
    }
    return []
  }

  /** 将会话列表写回 localStorage（按 userId 分桶）。 */
  function persistSessionsToStorage (sessions) {
    if (!options.userId) return
    try {
      const key = sessionIdListStorageKey()
      const map = JSON.parse(localStorage.getItem(key)) || {}
      map[options.userId] = normalizeStoredSessionList(sessions).map(item => ({
        sessionId: item.sessionId,
        title: item.title || ''
      }))
      localStorage.setItem(key, JSON.stringify(map))
    } catch (e) {
      console.warn('persistSessionsToStorage', e)
    }
  }

  /** 用存储数据更新 ctrl.sessionList（{ id, title } 形态）。 */
  function applySessionListFromStoredSessions (sessions) {
    ctrl.sessionList = (sessions || []).map(item => ({
      id: item.sessionId,
      title: item.title || ''
    }))
  }

  /** 格式化为「年/月/日 时:分:秒」，用于默认会话标题等。 */
  function formatDate (date = new Date()) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`
  }

  /** 切换/新建会话视图时重置当前视图的流式合流缓冲（不清理其他会话的后台生成态）。 */
  function resetStreamingUiStateForViewChange () {
    ctrl.streamFinalized = true
    ctrl.userInitiatedStop = false
    ctrl.activeAssistantMessageIndex = -1
    ctrl.activeAssistantText = ''
    ctrl.streamingAssistantMessageIds = []
    ctrl.streamingUserMessageIds = []
    ctrl.assistantPartTextMap = {}
    ctrl.assistantPartOrder = []
    ctrl.assistantProcessPartsMap = {}
    ctrl.assistantProcessPartOrder = []
    ctrl.streamPartIdTypes = {}
  }

  /** 进入「无远程会话」的空视图：可选刷新侧栏列表、消息恢复为 initialMessages。 */
  function prepareEmptyEntryView (shouldRefreshSessionList = true) {
    resetStreamingUiStateForViewChange()
    ctrl.activeSessionId = null
    if (shouldRefreshSessionList) {
      refreshSessionListFromStorage()
    }
    ctrl.messages = cloneInitialMessages(options.initialMessages)
  }

  /** 懒创建 OpenCode HTTP 客户端并启动全局 SSE（若尚未启动且未 dispose）。 */
  function ensureOpenCodeClient () {
    if (disposed) return
    if (ctrl.opencodeClient) return
    ctrl.opencodeClient = createOpencodeClient({
      baseUrl: options.opencodeUrl,
      parseAs: 'json',
      directory: options.opencodeDirectory,
      headers: options.opencodeHeaders
    })
    startGlobalEventLoop()
  }

  /** 首条用户消息前向服务端创建会话，并写入 activeSessionId 与本地列表。 */
  async function createServerSessionForFirstSend () {
    ensureOpenCodeClient()
    if (!ctrl.opencodeClient) return false
    try {
      const result = await ctrl.opencodeClient.session.create({
        directory: options.opencodeDirectory
      })
      if (result && result.data && result.data.id) {
        ctrl.activeSessionId = result.data.id
        persistLastActiveSessionId(ctrl.activeSessionId)
        const title = result.data.title
          ? String(result.data.title).trim()
          : ('新会话-' + formatDate())
        ensureSessionInList(ctrl.activeSessionId, title)
        return true
      }
    } catch (e) {
      console.error('创建会话失败:', e)
      if (!showToast({ message: '创建会话失败，请稍后重试', position: 'bottom' })) {
        addSystemMessage('创建会话失败，请稍后重试。')
      }
      if (typeof options.onError === 'function') {
        options.onError(e)
      }
    }
    return false
  }

  /** 将会话置顶写入本地列表（可只传 sessionId 以保留原标题），并裁剪到 maxSessionCount。 */
  function ensureSessionInList (sessionId, title) {
    if (!sessionId) return
    let userSessions = getStoredSessions()
    const normalizedTitle = title ? String(title).trim() : ''
    const existsIndex = userSessions.findIndex(item => item.sessionId === sessionId)
    let nextItem = null
    if (existsIndex !== -1) {
      const removed = userSessions.splice(existsIndex, 1)
      if (removed && removed[0]) {
        nextItem = removed[0]
      }
    }
    nextItem = {
      sessionId,
      title: normalizedTitle || (nextItem && nextItem.title) || ('新会话-' + formatDate())
    }
    userSessions.unshift(nextItem)
    const max = options.maxSessionCount
    if (userSessions.length > max) {
      userSessions = userSessions.slice(0, max)
    }
    persistSessionsToStorage(userSessions)
    applySessionListFromStoredSessions(userSessions)
  }

  /** 仅从 localStorage 刷新 ctrl.sessionList。 */
  function refreshSessionListFromStorage () {
    const sessions = getStoredSessions()
    applySessionListFromStoredSessions(sessions)
  }

  /** 拉取服务端会话标题并更新本地存储与列表；失败时最多重试 2 次。 */
  async function syncSessionTitleFromServer (sessionId, retry = 0) {
    if (!ctrl.opencodeClient || !sessionId) return false
    try {
      const result = await ctrl.opencodeClient.session.get({
        sessionID: sessionId
      })
      const title = result && result.data && result.data.title
        ? String(result.data.title).trim()
        : ''
      if (title) {
        const ok = await updateSessionTitle(sessionId, title)
        if (ok && typeof options.onTitleSynced === 'function') {
          options.onTitleSynced({ sessionId, title })
        }
        return ok
      }
    } catch (error) {
      console.warn('获取会话标题失败:', error)
    }
    if (retry < 2) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return syncSessionTitleFromServer(sessionId, retry + 1)
    }
    return false
  }

  /** 按 sessionId 更新本地会话标题并刷新 sessionList。 */
  async function updateSessionTitle (sessionId, title) {
    if (!sessionId) return false
    const normalizedTitle = title ? String(title).trim() : ''
    if (!normalizedTitle) return false
    const sessions = getStoredSessions()
    const sessionIndex = sessions.findIndex(item => item.sessionId === sessionId)
    if (sessionIndex === -1) {
      return false
    }
    sessions[sessionIndex] = Object.assign({}, sessions[sessionIndex], {
      title: normalizedTitle
    })
    persistSessionsToStorage(sessions)
    applySessionListFromStoredSessions(sessions)
    return true
  }

  /** 流式 part 是否在 UI 上与历史 formatMessages 一致展示（text / reasoning）。 */
  function isStreamVisiblePartType (type) {
    return type === 'text' || type === 'reasoning'
  }

  function isProcessPartType (type) {
    return !!type && !isStreamVisiblePartType(type)
  }

  function ensureSessionPartTypeMap (sessionID) {
    if (!sessionID) return {}
    let typesMap = ctrl.streamPartTypesBySession[sessionID]
    if (!typesMap) {
      Vue.set(ctrl.streamPartTypesBySession, sessionID, {})
      typesMap = ctrl.streamPartTypesBySession[sessionID]
    }
    return typesMap
  }

  function recordSessionMessageRole (sessionID, messageID, role) {
    if (!sessionID || !messageID || !role) return
    let rolesMap = ctrl.messageRoleBySession[sessionID]
    if (!rolesMap) {
      Vue.set(ctrl.messageRoleBySession, sessionID, {})
      rolesMap = ctrl.messageRoleBySession[sessionID]
    }
    Vue.set(rolesMap, messageID, role)
  }

  function getSessionMessageRole (sessionID, messageID) {
    if (!sessionID || !messageID) return ''
    const rolesMap = ctrl.messageRoleBySession[sessionID]
    if (!rolesMap) return ''
    return rolesMap[messageID] || ''
  }

  function registerChildSessionBindingFromPart (part) {
    const state = part && part.state && typeof part.state === 'object' ? part.state : null
    const metadata = state && state.metadata && typeof state.metadata === 'object' ? state.metadata : null
    const childSessionId = metadata && (metadata.sessionId || metadata.sessionID)
    if (!childSessionId) return null
    Vue.set(ctrl.childSessionBindingsBySession, childSessionId, {
      parentSessionId: ctrl.activeSessionId || metadata.parentSessionId || metadata.parentSessionID || '',
      title: state.title || part.tool || '子任务',
      toolPartId: part.id || ''
    })
    if (!ctrl.messageRoleBySession[childSessionId]) {
      Vue.set(ctrl.messageRoleBySession, childSessionId, {})
    }
    return childSessionId
  }

  function getBoundChildSession (sessionID) {
    if (!sessionID) return null
    const binding = ctrl.childSessionBindingsBySession[sessionID]
    if (!binding) return null
    if (binding.parentSessionId && ctrl.activeSessionId && binding.parentSessionId !== ctrl.activeSessionId) {
      return null
    }
    return binding
  }

  function buildChildProcessPartId (sessionID, messageID, partID, fallbackType) {
    return [
      'child',
      sessionID || 'session',
      messageID || 'message',
      partID || fallbackType || 'part'
    ].join(':')
  }


  /** 按 messageId 查找当前消息列表里的气泡索引。 */
  function findMessageIndexById (messageID) {
    if (!messageID) return -1
    return ctrl.messages.findIndex(message => message && message.messageId === messageID)
  }

  /**
   * 把服务端 user messageID 绑定到本地刚发送、尚未拿到 messageId 的最后一条用户消息上，避免重复气泡。
   */
  function bindPendingUserMessageId (messageID) {
    if (!messageID || !ctrl.messages.length) return -1
    const lastIndex = ctrl.messages.length - 1
    const lastMessage = ctrl.messages[lastIndex]
    if (lastMessage && lastMessage.role === 'user' && !lastMessage.messageId) {
      Vue.set(lastMessage, 'messageId', messageID)
      return lastIndex
    }
    return -1
  }

  /** 确保当前 messageID 对应一条用户气泡存在。 */
  function ensureUserMessageBubble (messageID, initialText = '') {
    let index = findMessageIndexById(messageID)
    if (index !== -1) {
      return index
    }
    index = bindPendingUserMessageId(messageID)
    if (index !== -1) {
      const message = ctrl.messages[index]
      if (message && !message.content && initialText) {
        message.content = initialText
      }
      return index
    }
    return ctrl.messages.push({
      role: 'user',
      content: initialText || '',
      messageId: messageID
    }) - 1
  }

  /** 把 user 类增量文本直接拼到对应用户气泡。 */
  function appendUserDelta (messageID, delta) {
    if (!messageID || !delta) return
    const index = ensureUserMessageBubble(messageID)
    const message = ctrl.messages[index]
    if (!message) return
    const current = message.content || ''
    let next = current + delta
    if (!current) {
      next = String(next).replace(/^\s+/, '')
    }
    message.content = next
  }

  /** 用全量文本覆盖对应用户气泡内容。 */
  function replaceUserContent (messageID, text) {
    if (!messageID) return
    const index = ensureUserMessageBubble(messageID)
    const message = ctrl.messages[index]
    if (!message) return
    let next = text || ''
    if (!(message.content || '').trim()) {
      next = String(next).replace(/^\s+/, '')
    }
    message.content = next
  }

  /** 确保当前轮有一条空的 assistant 气泡用于承接流式正文。 */
  function ensureActiveAssistantBubble () {
    if (ctrl.activeAssistantMessageIndex >= 0 && ctrl.messages[ctrl.activeAssistantMessageIndex]) return
    ctrl.activeAssistantMessageIndex = ctrl.messages.push({
      role: 'assistant',
      content: '',
      reasoning: '',
      processParts: []
    }) - 1
  }

  /** 无 partID 时把增量文本拼到当前 assistant 正文（纯文本累积）。 */
  function appendAssistantDelta (delta) {
    if (!delta) return
    let normalizedDelta = delta
    if (!ctrl.activeAssistantText) {
      normalizedDelta = String(delta).replace(/^\s+/, '')
    }
    if (!normalizedDelta) return
    if (ctrl.activeSessionId) {
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, true)
      syncLoadingFlags()
    }
    ensureActiveAssistantBubble()
    ctrl.activeAssistantText += normalizedDelta
    if (ctrl.messages[ctrl.activeAssistantMessageIndex]) {
      ctrl.messages[ctrl.activeAssistantMessageIndex].content = ctrl.activeAssistantText
    }
  }

  /** 按 part 顺序与类型（text/reasoning）重建当前 assistant 气泡的 content / reasoning。 */
  function rebuildAssistantMessageFromPartMaps () {
    const textChunks = []
    const reasoningChunks = []
    for (const id of ctrl.assistantPartOrder) {
      const t = (id && ctrl.streamPartIdTypes[id]) || 'text'
      const chunk = ctrl.assistantPartTextMap[id] != null
        ? String(ctrl.assistantPartTextMap[id])
        : ''
      if (t === 'reasoning') {
        reasoningChunks.push(chunk)
      } else {
        textChunks.push(chunk)
      }
    }
    const reasoningRaw = reasoningChunks.join('\n\n')
    const textRaw = textChunks.join('\n')
    ctrl.activeAssistantText = textRaw
    if (ctrl.activeSessionId && (reasoningRaw.trim() || textRaw.trim())) {
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, true)
      syncLoadingFlags()
    }
    ensureActiveAssistantBubble()
    const m = ctrl.messages[ctrl.activeAssistantMessageIndex]
    if (m) {
      const rN = normalizeReasoningPlain(reasoningRaw.trim())
      m.reasoning = rN || ''
      m.content = textRaw.trim() ? textRaw.trim() : ''
      m.processParts = ctrl.assistantProcessPartOrder
        .map(id => ctrl.assistantProcessPartsMap[id])
        .filter(Boolean)
    }
  }

  function upsertAssistantProcessPart (part) {
    if (!part || !part.id) return
    if (ctrl.assistantProcessPartOrder.indexOf(part.id) === -1) {
      ctrl.assistantProcessPartOrder.push(part.id)
    }
    const normalized = normalizeProcessPart(part)
    const current = ctrl.assistantProcessPartsMap[part.id] || null
    if (!normalized.sourceSessionId && current && current.sourceSessionId) {
      normalized.sourceSessionId = current.sourceSessionId
    }
    if (!normalized.sourceTitle && current && current.sourceTitle) {
      normalized.sourceTitle = current.sourceTitle
    }
    Vue.set(ctrl.assistantProcessPartsMap, part.id, normalized)
    if (ctrl.activeSessionId) {
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, true)
      syncLoadingFlags()
    }
    rebuildAssistantMessageFromPartMaps()
  }

  function upsertAssistantProcessTextPart (part, appendMode) {
    if (!part || !part.id) return
    if (ctrl.assistantProcessPartOrder.indexOf(part.id) === -1) {
      ctrl.assistantProcessPartOrder.push(part.id)
    }
    const current = ctrl.assistantProcessPartsMap[part.id] || null
    const normalized = normalizeProcessPart(part)
    const currentText = current && current.text ? String(current.text) : ''
    let nextText = normalized.text || ''
    if (appendMode) {
      nextText = currentText + nextText
      if (!currentText) {
        nextText = String(nextText).replace(/^\s+/, '')
      }
    }
    normalized.text = nextText
    if (!normalized.sourceSessionId && current && current.sourceSessionId) {
      normalized.sourceSessionId = current.sourceSessionId
    }
    if (!normalized.sourceTitle && current && current.sourceTitle) {
      normalized.sourceTitle = current.sourceTitle
    }
    if (!normalized.state && current && current.state) {
      normalized.state = current.state
    }
    if (!normalized.status && current && current.status) {
      normalized.status = current.status
    }
    if (!normalized.title && current && current.title) {
      normalized.title = current.title
    }
    Vue.set(ctrl.assistantProcessPartsMap, part.id, normalized)
    if (ctrl.activeSessionId) {
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, true)
      syncLoadingFlags()
    }
    rebuildAssistantMessageFromPartMaps()
  }

  function relayBoundChildSessionEvent (ev, sessionID) {
    const binding = getBoundChildSession(sessionID)
    if (!binding) return false

    if (ev.type === 'message.update' || ev.type === 'message.updated') {
      const role = ev.properties.role || (ev.properties.info && ev.properties.info.role)
      const messageID = ev.properties.messageID || ev.properties.id || (ev.properties.info && ev.properties.info.id)
      if (messageID && role) {
        recordSessionMessageRole(sessionID, messageID, role)
      }
      return true
    }

    if (ev.type === 'message.part.delta' || ev.type === 'message.part.update' || ev.type === 'message.part.updated') {
      const part = ev.properties.part || {}
      const messageID = ev.properties.messageID || part.messageID || ''
      const role = getSessionMessageRole(sessionID, messageID)
      if (messageID && role === 'user') {
        return true
      }
      const partID = ev.properties.partID || part.id || ''
      const typesMap = ensureSessionPartTypeMap(sessionID)
      if (part.id && part.type) {
        Vue.set(typesMap, part.id, part.type)
      } else if (partID && part.type) {
        Vue.set(typesMap, partID, part.type)
      }
      const partType = part.type || (partID && typesMap[partID])
      if (messageID && !role && partType && !isProcessPartType(partType)) {
        return true
      }
      if (partType && isProcessPartType(partType)) {
        registerChildSessionBindingFromPart(part)
        upsertAssistantProcessPart(Object.assign({}, part, {
          id: buildChildProcessPartId(sessionID, messageID, partID || part.callID, partType),
          type: partType,
          sourceSessionId: sessionID,
          sourceTitle: binding.title
        }))
        return true
      }
      if (partType && !isStreamVisiblePartType(partType)) {
        return true
      }
      const text = ev.type === 'message.part.delta'
        ? (ev.properties.delta || (ev.properties.part && ev.properties.part.delta) || '')
        : (part.text || '')
      upsertAssistantProcessTextPart({
        id: buildChildProcessPartId(sessionID, messageID, partID, partType === 'reasoning' ? 'subsession-reasoning' : 'subsession-text'),
        type: partType === 'reasoning' ? 'subsession-reasoning' : 'subsession-text',
        text,
        sourceSessionId: sessionID,
        sourceTitle: binding.title,
        title: binding.title
      }, ev.type === 'message.part.delta')
      return true
    }

    return false
  }

  /** 按 partID 更新流式片段文本；appendMode 为 true 时在末尾拼接 delta。 */
  function upsertAssistantPart (partID, text, appendMode) {
    if (!partID) return
    if (ctrl.assistantPartOrder.indexOf(partID) === -1) {
      ctrl.assistantPartOrder.push(partID)
    }
    const current = ctrl.assistantPartTextMap[partID] || ''
    let next = appendMode ? (current + (text || '')) : (text || '')
    if (!current) {
      next = String(next).replace(/^\s+/, '')
    }
    Vue.set(ctrl.assistantPartTextMap, partID, next)
    rebuildAssistantMessageFromPartMaps()
  }

  /** 用全量文本替换当前 assistant 正文（part.update 无 id 等场景）。 */
  function replaceAssistantContent (text) {
    ensureActiveAssistantBubble()
    let nextText = text || ''
    if (!ctrl.activeAssistantText) {
      nextText = String(nextText).replace(/^\s+/, '')
    }
    ctrl.activeAssistantText = nextText
    if (ctrl.activeAssistantText && ctrl.activeSessionId) {
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, true)
      syncLoadingFlags()
    }
    if (ctrl.messages[ctrl.activeAssistantMessageIndex]) {
      ctrl.messages[ctrl.activeAssistantMessageIndex].content = ctrl.activeAssistantText
    }
  }

  /** 当前轮流式正常结束：清生成态、清 part 缓冲、复位索引。 */
  function markStreamDone () {
    if (ctrl.streamFinalized) return
    ctrl.streamFinalized = true
    if (ctrl.activeSessionId) {
      Vue.set(ctrl.activeGenerationBySession, ctrl.activeSessionId, false)
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, false)
      Vue.delete(ctrl.streamPartTypesBySession, ctrl.activeSessionId)
    }
    ctrl.activeAssistantMessageIndex = -1
    ctrl.activeAssistantText = ''
    ctrl.streamingAssistantMessageIds = []
    ctrl.streamingUserMessageIds = []
    ctrl.assistantPartTextMap = {}
    ctrl.assistantPartOrder = []
    ctrl.assistantProcessPartsMap = {}
    ctrl.assistantProcessPartOrder = []
    ctrl.streamPartIdTypes = {}
    syncLoadingFlags()
  }

  /** 用户停止或 abort 类结束：不展示失败气泡，空回复时写入「已停止生成」。 */
  function markStreamAborted () {
    ctrl.userInitiatedStop = false
    if (ctrl.activeSessionId) {
      Vue.set(ctrl.activeGenerationBySession, ctrl.activeSessionId, false)
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, false)
      Vue.delete(ctrl.streamPartTypesBySession, ctrl.activeSessionId)
    }
    if (ctrl.streamFinalized) {
      syncLoadingFlags()
      return
    }
    ctrl.streamFinalized = true
    const lastMessage = ctrl.messages.length ? ctrl.messages[ctrl.messages.length - 1] : null
    if (lastMessage && lastMessage.role === 'assistant') {
      const plain = messageHtmlToPlain(lastMessage.content) + messageHtmlToPlain(lastMessage.reasoning)
      if (!plain.trim()) {
        lastMessage.content = '已停止生成'
      }
    }
    ctrl.activeAssistantMessageIndex = -1
    ctrl.activeAssistantText = ''
    ctrl.streamingAssistantMessageIds = []
    ctrl.streamingUserMessageIds = []
    ctrl.assistantPartTextMap = {}
    ctrl.assistantPartOrder = []
    ctrl.assistantProcessPartsMap = {}
    ctrl.assistantProcessPartOrder = []
    ctrl.streamPartIdTypes = {}
    syncLoadingFlags()
  }

  /** 非中止类错误：结束生成态并在最后一条 assistant 或新气泡中展示错误说明。 */
  function markStreamFailed (error) {
    if (ctrl.streamFinalized) return
    if (ctrl.userInitiatedStop || isStreamAbortError(error)) {
      markStreamAborted()
      return
    }
    ctrl.streamFinalized = true
    if (ctrl.activeSessionId) {
      Vue.set(ctrl.activeGenerationBySession, ctrl.activeSessionId, false)
      Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, false)
      Vue.delete(ctrl.streamPartTypesBySession, ctrl.activeSessionId)
    }
    const lastMessage = ctrl.messages.length ? ctrl.messages[ctrl.messages.length - 1] : null
    const errMsg = (error && error.message) ? error.message : String(error || '')
    if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.content) {
      lastMessage.content = '抱歉，处理你的请求时出现了问题。请稍后重试。<br>错误信息：' + errMsg
    } else {
      ctrl.messages.push({
        role: 'assistant',
        content: '抱歉，处理你的请求时出现了问题。请稍后重试。<br>错误信息：' + errMsg
      })
    }
    ctrl.activeAssistantMessageIndex = -1
    ctrl.activeAssistantText = ''
    ctrl.streamingAssistantMessageIds = []
    ctrl.streamingUserMessageIds = []
    ctrl.assistantPartTextMap = {}
    ctrl.assistantPartOrder = []
    ctrl.assistantProcessPartsMap = {}
    ctrl.assistantProcessPartOrder = []
    ctrl.streamPartIdTypes = {}
    syncLoadingFlags()
    if (typeof options.onError === 'function') {
      options.onError(error instanceof Error ? error : new Error(errMsg))
    }
  }

  /** 追加一条 assistant 形态的提示文案（如连接失败、建会话失败）。 */
  function addSystemMessage (content) {
    ctrl.messages.push({
      role: 'assistant',
      content: content
    })
  }

  /** 非当前查看会话上的 SSE：只维护该会话的「已出字」等侧栏状态，不写当前 messages。 */
  function onBackgroundSessionEvent (ev, sessionID) {
    if (ev.type === 'message.update' || ev.type === 'message.updated') {
      const role = ev.properties.role || (ev.properties.info && ev.properties.info.role)
      const messageID = ev.properties.messageID || ev.properties.id || (ev.properties.info && ev.properties.info.id)
      if (messageID && role) {
        recordSessionMessageRole(sessionID, messageID, role)
      }
      return
    }
    if (ev.type === 'message.part.delta') {
      const messageID = ev.properties.messageID || (ev.properties.part && ev.properties.part.messageID)
      if (messageID && ctrl.streamingUserMessageIds.indexOf(messageID) !== -1) {
        return
      }
      const p = ev.properties.part || {}
      const partID = ev.properties.partID || p.id
      let typesMap = ctrl.streamPartTypesBySession[sessionID]
      if (!typesMap) {
        Vue.set(ctrl.streamPartTypesBySession, sessionID, {})
        typesMap = ctrl.streamPartTypesBySession[sessionID]
      }
      if (p.id && p.type) {
        Vue.set(typesMap, p.id, p.type)
      } else if (partID && p.type) {
        Vue.set(typesMap, partID, p.type)
      }
      const partType = p.type || (partID && typesMap[partID])
      if (partType && isProcessPartType(partType)) {
        Vue.set(ctrl.hasReplyContentBySession, sessionID, true)
        return
      }
      if (partType && !isStreamVisiblePartType(partType)) {
        return
      }
      const deltaText = ev.properties.delta || (ev.properties.part && ev.properties.part.delta) || ''
      if (String(deltaText).trim()) {
        Vue.set(ctrl.hasReplyContentBySession, sessionID, true)
      }
      return
    }
    if (ev.type === 'message.part.update' || ev.type === 'message.part.updated') {
      const part = ev.properties.part || {}
      let typesMap = ctrl.streamPartTypesBySession[sessionID]
      if (!typesMap) {
        Vue.set(ctrl.streamPartTypesBySession, sessionID, {})
        typesMap = ctrl.streamPartTypesBySession[sessionID]
      }
      if (part.id && part.type) {
        Vue.set(typesMap, part.id, part.type)
      }
      if (part.type && isProcessPartType(part.type)) {
        Vue.set(ctrl.hasReplyContentBySession, sessionID, true)
        return
      }
      if (!part.type || !isStreamVisiblePartType(part.type)) {
        return
      }
      if ((part.text || '').trim()) {
        Vue.set(ctrl.hasReplyContentBySession, sessionID, true)
      }
    }
  }

  /** 处理 global/event SSE 解包后的一条事件（当前会话写 messages，其它会话走后台分支）。 */
  function handleGlobalEvent (ev) {
    if (!ev || !ev.type || !ev.properties) return
    const sessionID = ev.properties.sessionID || (ev.properties.info && ev.properties.info.sessionID) || (ev.properties.part && ev.properties.part.sessionID)

    if (ev.type === 'session.idle') {
      if (sessionID) {
        Vue.set(ctrl.activeGenerationBySession, sessionID, false)
        Vue.set(ctrl.hasReplyContentBySession, sessionID, false)
        Vue.delete(ctrl.streamPartTypesBySession, sessionID)
      }
      if (!sessionID || sessionID === ctrl.activeSessionId) {
        markStreamDone()
      }
      syncLoadingFlags()
      if (ctrl.isFirstUserMessage && ctrl.activeSessionId) {
        syncSessionTitleFromServer(ctrl.activeSessionId)
        ctrl.isFirstUserMessage = false
      }
      if (typeof options.onSessionIdle === 'function') {
        options.onSessionIdle({ sessionId: sessionID || ctrl.activeSessionId })
      }
      return
    }

    if (ev.type === 'session.error') {
      if (sessionID) {
        Vue.set(ctrl.activeGenerationBySession, sessionID, false)
        Vue.set(ctrl.hasReplyContentBySession, sessionID, false)
        Vue.delete(ctrl.streamPartTypesBySession, sessionID)
      }
      let msg = '会话流式处理失败'
      if (sessionID && sessionID !== ctrl.activeSessionId) {
        const err = ev.properties.error
        if (typeof err === 'string') {
          msg = err
        } else if (err && err.message) {
          msg = err.message
        } else if (ev.properties.message) {
          msg = ev.properties.message
        } else if (ev.properties.error && ev.properties.error.data && ev.properties.error.data.message) {
          msg = ev.properties.error.data.message
        }
        const childBinding = getBoundChildSession(sessionID)
        if (childBinding) {
          upsertAssistantProcessTextPart({
            id: buildChildProcessPartId(sessionID, 'session-error', 'session-error', 'subsession-status'),
            type: 'subsession-status',
            text: msg,
            sourceSessionId: sessionID,
            sourceTitle: childBinding.title,
            title: childBinding.title
          }, false)
        }
        syncLoadingFlags()
        return
      }
      const err = ev.properties.error
      if (typeof err === 'string') {
        msg = err
      } else if (err && err.message) {
        msg = err.message
      } else if (ev.properties.message) {
        msg = ev.properties.message
      } else if (ev.properties.error && ev.properties.error.data && ev.properties.error.data.message) {
        msg = ev.properties.error.data.message
      }
      if (isStreamAbortError(new Error(String(msg || ''))) || ctrl.userInitiatedStop) {
        markStreamAborted()
        return
      }
      markStreamFailed(new Error(msg))
      return
    }

    if (sessionID && sessionID !== ctrl.activeSessionId) {
      if (relayBoundChildSessionEvent(ev, sessionID)) {
        return
      }
      onBackgroundSessionEvent(ev, sessionID)
      return
    }

    if (ev.type === 'message.update' || ev.type === 'message.updated') {
      const role = ev.properties.role || (ev.properties.info && ev.properties.info.role)
      const messageID = ev.properties.messageID || ev.properties.id || (ev.properties.info && ev.properties.info.id)
      if (role === 'assistant' && messageID) {
        if (ctrl.streamingAssistantMessageIds.indexOf(messageID) === -1) {
          ctrl.streamingAssistantMessageIds.push(messageID)
        }
      } else if (role === 'user' && messageID) {
        bindPendingUserMessageId(messageID)
        if (ctrl.streamingUserMessageIds.indexOf(messageID) === -1) {
          ctrl.streamingUserMessageIds.push(messageID)
        }
      }
      return
    }

    if (ev.type === 'message.part.delta') {
      const messageID = ev.properties.messageID || (ev.properties.part && ev.properties.part.messageID)
      if (messageID && ctrl.streamingUserMessageIds.indexOf(messageID) !== -1) {
        appendUserDelta(messageID, ev.properties.delta || (ev.properties.part && ev.properties.part.delta) || '')
        return
      }
      if (messageID) {
        if (ctrl.streamingAssistantMessageIds.indexOf(messageID) === -1) {
          if (ctrl.activeSessionId && ctrl.activeGenerationBySession[ctrl.activeSessionId]) {
            ctrl.streamingAssistantMessageIds.push(messageID)
          }
        }
      } else if (!ctrl.streamingAssistantMessageIds.length) {
        if (!(ctrl.activeSessionId && ctrl.activeGenerationBySession[ctrl.activeSessionId])) {
          return
        }
      }
      const p = ev.properties.part || {}
      const partID = ev.properties.partID || p.id
      if (p.id && p.type) {
        Vue.set(ctrl.streamPartIdTypes, p.id, p.type)
      } else if (partID && p.type) {
        Vue.set(ctrl.streamPartIdTypes, partID, p.type)
      }
      const partType = p.type || (partID && ctrl.streamPartIdTypes[partID])
      if (partType && isProcessPartType(partType)) {
        if (partID) {
          registerChildSessionBindingFromPart(p)
          upsertAssistantProcessPart(Object.assign({}, p, { id: partID, type: partType }))
        }
        return
      }
      if (partType && !isStreamVisiblePartType(partType)) {
        return
      }
      const deltaText = ev.properties.delta || (ev.properties.part && ev.properties.part.delta) || ''
      if (partID) {
        upsertAssistantPart(partID, deltaText, true)
      } else {
        appendAssistantDelta(deltaText)
      }
      return
    }

    if (ev.type === 'message.part.update' || ev.type === 'message.part.updated') {
      const part = ev.properties.part || {}
      const messageID = part.messageID || ev.properties.messageID
      if (messageID && ctrl.streamingUserMessageIds.indexOf(messageID) !== -1) {
        replaceUserContent(messageID, part.text || '')
        return
      }
      if (messageID) {
        if (ctrl.streamingAssistantMessageIds.indexOf(messageID) === -1) {
          if (ctrl.activeSessionId && ctrl.activeGenerationBySession[ctrl.activeSessionId]) {
            ctrl.streamingAssistantMessageIds.push(messageID)
          } else {
            return
          }
        }
      } else if (!ctrl.streamingAssistantMessageIds.length) {
        if (!(ctrl.activeSessionId && ctrl.activeGenerationBySession[ctrl.activeSessionId])) {
          return
        }
      }
      if (part.id) {
        Vue.set(ctrl.streamPartIdTypes, part.id, part.type)
      }
      if (part.type && isProcessPartType(part.type)) {
        registerChildSessionBindingFromPart(part)
        upsertAssistantProcessPart(part)
        return
      }
      if (!part.type || !isStreamVisiblePartType(part.type)) {
        return
      }
      if (part.id) {
        upsertAssistantPart(part.id, part.text || '', false)
      } else {
        replaceAssistantContent(part.text || '')
      }
    }
  }

  /** 订阅 OpenCode 全局事件流并分发给 handleGlobalEvent（避免 for-await 以兼容未转译 node_modules 的老构建）。 */
  async function startGlobalEventLoop () {
    if (!ctrl.opencodeClient || ctrl.sseAbortController || disposed) return
    const controller = new AbortController()
    ctrl.sseAbortController = controller
    try {
      const { stream } = await ctrl.opencodeClient.global.event({
        signal: controller.signal,
        onSseError: (err) => {
          console.error('SSE 错误:', err)
        }
      })
      const iterator = stream[Symbol.asyncIterator]()
      for (;;) {
        const step = await iterator.next()
        if (disposed) break
        if (step.done) break
        const wrapped = unwrapGlobalEventRow(step.value)
        handleGlobalEvent(wrapped.event)
      }
    } catch (error) {
      if (!(error && error.name === 'AbortError')) {
        console.error('全局事件循环异常:', error)
      }
    } finally {
      ctrl.sseAbortController = null
    }
  }

  /** 从服务端拉取指定会话历史并 format 后写入 ctrl.messages。 */
  async function loadSessionMessages (sessionId) {
    try {
      const messagesResult = await ctrl.opencodeClient.session.messages({
        sessionID: sessionId,
        directory: options.opencodeDirectory
      })
      const messages = formatMessages(messagesResult.data)
      if (messages.length > 0) {
        ctrl.messages = messages
      } else {
        ctrl.messages = cloneInitialMessages(options.initialMessages)
      }
    } catch (error) {
      console.error('加载会话消息失败:', error)
      ctrl.messages = cloneInitialMessages(options.initialMessages)
    }
  }

  /** 删除远程会话与本地存储项；若删的是当前会话则切换到列表首条或空视图。 */
  async function performDeleteSession (session) {
    const id = resolveSessionId(session)
    if (!id) return
    try {
      if (ctrl.opencodeClient) {
        await ctrl.opencodeClient.session.delete({ sessionID: id })
      }
    } catch (error) {
      console.error('删除远程会话失败:', error)
    }
    const wasCurrent = ctrl.activeSessionId === id
    const userSessions = getStoredSessions().filter(item => item.sessionId !== id)
    persistSessionsToStorage(userSessions)
    applySessionListFromStoredSessions(userSessions)
    if (id === readLastActiveSessionId()) {
      clearLastActiveSessionId()
    }

    if (!wasCurrent) return
    if (ctrl.sessionList.length > 0) {
      await selectSession(ctrl.sessionList[0].id)
    } else {
      clearLastActiveSessionId()
      resetStreamingUiStateForViewChange()
      ctrl.activeSessionId = null
      ctrl.messages = cloneInitialMessages(options.initialMessages)
    }
    syncLoadingFlags()
  }

  /** 切换到指定会话：重置流式 UI、拉历史、持久化 lastActive。 */
  async function selectSession (sessionOrId) {
    const id = resolveSessionId(sessionOrId)
    if (!id) return
    resetStreamingUiStateForViewChange()
    ctrl.activeSessionId = id
    await loadSessionMessages(id)
    persistLastActiveSessionId(id)
    syncLoadingFlags()
  }

  /** 先 confirm 再执行 performDeleteSession。 */
  async function deleteSession (sessionOrId) {
    const id = resolveSessionId(sessionOrId)
    if (!id) return
    const ok = await confirmDelete({
      message: '确定删除该会话？',
      title: ''
    })
    if (!ok) return
    await performDeleteSession(id)
  }

  /** 等同侧栏「新建」：清 lastActive、本地空草稿，不请求服务端建会话。 */
  function startNewDraft () {
    ensureOpenCodeClient()
    clearLastActiveSessionId()
    prepareEmptyEntryView(false)
    syncLoadingFlags()
  }

  /** 为当前 activeSessionId 重新拉取服务端消息列表。 */
  async function reloadMessages () {
    if (!ctrl.activeSessionId || !ctrl.opencodeClient) return
    await loadSessionMessages(ctrl.activeSessionId)
  }

  /** 请求服务端 abort 并本地 markStreamAborted，停止当前会话生成。 */
  async function abort () {
    if (!ctrl.isCurrentSessionLoading) return
    ctrl.userInitiatedStop = true
    if (ctrl.opencodeClient && ctrl.activeSessionId) {
      try {
        await ctrl.opencodeClient.session.abort({
          sessionID: ctrl.activeSessionId
        })
      } catch (error) {
        console.error('停止生成失败:', error)
        showToast({
          message: (error && error.message) || '停止失败，请稍后重试',
          position: 'bottom'
        })
      }
    }
    markStreamAborted()
  }

  /**
   * 校验并发送用户消息：必要时建远程会话、推入 user 气泡、调用 promptAsync；失败时 markStreamFailed 并 rethrow。
   * @param {string|{ text?: string, system?: string, agent?: string, model?: { providerID?: string, modelID?: string } }} input
   * @returns {Promise<void>}
   */
  async function sendMessage (input) {
    const opts = typeof input === 'string' ? { text: input } : (input || {})
    const message = (opts.text != null ? String(opts.text) : '').trim()
    if (!message) {
      throw new Error('消息不能为空')
    }
    if (ctrl.isCurrentSessionLoading) {
      throw new Error('当前会话正在生成中')
    }

    ensureOpenCodeClient()
    if (!ctrl.opencodeClient) {
      addSystemMessage('无法连接 AI 服务，请稍后再试')
      throw new Error('OpenCode 客户端未就绪')
    }

    if (!ctrl.activeSessionId) {
      const created = await createServerSessionForFirstSend()
      if (!created) {
        throw new Error('创建会话失败')
      }
    }

    ensureSessionInList(ctrl.activeSessionId)
    ctrl.isFirstUserMessage = ctrl.messages.filter(m => m.role === 'user').length === 0

    ctrl.messages.push({
      role: 'user',
      content: message
    })

    Vue.set(ctrl.activeGenerationBySession, ctrl.activeSessionId, true)
    Vue.set(ctrl.hasReplyContentBySession, ctrl.activeSessionId, false)
    ctrl.streamFinalized = false
    ctrl.userInitiatedStop = false
    syncLoadingFlags()

    try {
      ctrl.activeAssistantMessageIndex = -1
      ctrl.activeAssistantText = ''
      ctrl.streamingAssistantMessageIds = []
      ctrl.streamingUserMessageIds = []
      ctrl.assistantPartTextMap = {}
      ctrl.assistantPartOrder = []
      ctrl.assistantProcessPartsMap = {}
      ctrl.assistantProcessPartOrder = []
      ctrl.streamPartIdTypes = {}
      const system = opts.system != null ? opts.system : options.opencodeSystem
      const model = opts.model && typeof opts.model === 'object'
        ? {
            providerID: opts.model.providerID != null ? opts.model.providerID : options.opencodeProviderID,
            modelID: opts.model.modelID != null ? opts.model.modelID : options.opencodeModelID
          }
        : {
            providerID: options.opencodeProviderID,
            modelID: options.opencodeModelID
          }
      const body = {
        agent: opts.agent != null ? opts.agent : options.opencodeAgent,
        parts: [{ type: 'text', text: message }],
        model,
        system
      }
      await ctrl.opencodeClient.session.promptAsync(
        Object.assign({ sessionID: ctrl.activeSessionId }, body)
      )
    } catch (error) {
      console.error('发送消息失败:', error)
      markStreamFailed(error)
      throw error
    }
  }

  /** 侧栏展示用：返回会话标题或「未命名会话」。 */
  function getSessionTitle (session) {
    if (!session) return '未命名会话'
    return session.title || '未命名会话'
  }

  /** 指定会话是否仍在生成；不传 sessionId 时看当前 activeSessionId。 */
  function isSessionGenerating (sessionId) {
    const id =
      sessionId != null && sessionId !== ''
        ? resolveSessionId(sessionId)
        : ctrl.activeSessionId
    return !!(id && ctrl.activeGenerationBySession[id])
  }

  /** 将 Markdown 文本用与面板一致的 markdown-it 规则转为 HTML（宿主自行 v-html 需注意 XSS）。 */
  function renderMarkdownHtml (text) {
    const raw = text != null ? String(text) : ''
    return md.render(raw)
  }

  /** 创建客户端、启动 SSE、进入空入口视图；dispose 后可再次调用以恢复可用。 */
  async function init () {
    if (disposed) {
      disposed = false
    }
    try {
      ensureOpenCodeClient()
      prepareEmptyEntryView()
      syncLoadingFlags()
    } catch (error) {
      console.error('初始化 OpenCode 客户端失败:', error)
      addSystemMessage('无法连接到 AI 服务，请检查服务是否已启动')
      if (typeof options.onError === 'function') {
        options.onError(error)
      }
    }
  }

  /** 中止全局 SSE、清空客户端引用、复位流式状态；页面卸载时应调用。 */
  function dispose () {
    disposed = true
    if (ctrl.sseAbortController) {
      try {
        ctrl.sseAbortController.abort()
      } catch (e) {
        console.warn('dispose abort', e)
      }
      ctrl.sseAbortController = null
    }
    ctrl.opencodeClient = null
    resetStreamingUiStateForViewChange()
    syncLoadingFlags()
  }

  ctrl.sendMessage = sendMessage
  ctrl.abort = abort
  ctrl.selectSession = selectSession
  ctrl.switchSession = selectSession
  ctrl.deleteSession = deleteSession
  ctrl.startNewDraft = startNewDraft
  ctrl.refreshSessionList = refreshSessionListFromStorage
  ctrl.reloadMessages = reloadMessages
  ctrl.init = init
  ctrl.dispose = dispose
  ctrl.getSessionTitle = getSessionTitle
  ctrl.isSessionGenerating = isSessionGenerating
  ctrl.renderMarkdownHtml = renderMarkdownHtml

  return ctrl
}
